import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";

import MDBox from "theme/components/MDBox";
import MDButton from "theme/components/MDButton";
import MDTypography from "theme/components/MDTypography";

function WhatsAppTemplateComposer({
  open,
  onCancel,
  contact,
  templates,
  sendTemplate,
}) {
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [variableMappings, setVariableMappings] = useState({});

  const template = templates.find((item) => item.reference === selectedTemplate);

  const getTemplateVariables = (template) => {
    if (!template?.components) {
      return [];
    }

    const variables = [];

    template.components.forEach((component) => {
      const text = component.text || "";
      const matches = text.match(/{{([^{}]+)}}/g) || [];

      matches.forEach((match) => {
        const variable = match.replace("{{", "").replace("}}", "");

        if (!variables.includes(variable)) {
          variables.push(variable);
        }
      });
    });

    return variables;
  };

  const templateVariables = getTemplateVariables(template);

  useEffect(() => {
    if (!template) {
      setVariableMappings({});
      return;
    }

    const mappings = {};

    templateVariables.forEach((variable) => {
      mappings[variable] = {
        type: "",
        value: "",
      };
    });

    setVariableMappings(mappings);
  }, [selectedTemplate]);

  const handleTemplateChange = (event) => {
    setSelectedTemplate(event.target.value);
  };

  const handleVariableTypeChange = (variable, type) => {
    let value = "";

    if (type === "contact_name") {
      value = contact?.name || "";
    } else if (type === "contact_phone") {
      value = contact?.phone_number || "";
    }

    setVariableMappings((current) => ({
      ...current,
      [variable]: {
        type: type,
        value: value,
      },
    }));
  };

  const handleVariableValueChange = (variable, value) => {
    setVariableMappings((current) => ({
      ...current,
      [variable]: {
        ...current[variable],
        value: value,
      },
    }));
  };

  const getVariableValue = (variable) => {
    return variableMappings[variable]?.value || "";
  };

  const replaceVariables = (text) => {
    if (!text) {
      return "";
    }

    return text.replace(/{{([^{}]+)}}/g, (match, variable) => {
      const value = getVariableValue(variable);
      return value || match;
    });
  };

  const canSend = () => {
    if (!template) {
      return false;
    }

    for (const variable of templateVariables) {
      const mapping = variableMappings[variable];

      if (!mapping || !mapping.type) {
        return false;
      }

      if (mapping.type === "contact_name" && !contact?.name) {
        return false;
      }

      if (mapping.type === "contact_phone" && !contact?.phone_number) {
        return false;
      }

      if (mapping.type === "custom" && !mapping.value.trim()) {
        return false;
      }
    }

    return true;
  };

  const buildComponents = () => {
    if (!template?.components) {
      return [];
    }

    return template.components
      .map((component) => {
        const matches = (component.text || "").match(/{{([^{}]+)}}/g) || [];

        if (matches.length === 0) {
          return null;
        }

        return {
          type: component.type.toLowerCase(),
          parameters: matches.map((match) => {
            const variable = match.replace("{{", "").replace("}}", "");

            return {
              type: "text",
              text: getVariableValue(variable),
            };
          }),
        };
      })
      .filter((component) => component !== null);
  };

  const handleSend = () => {
    if (!canSend()) {
      return;
    }

    sendTemplate({
      template: template.reference,
      components: buildComponents(),
    });

    setSelectedTemplate("");
    setVariableMappings({});
  };

  const handleCancel = () => {
    setSelectedTemplate("");
    setVariableMappings({});
    onCancel();
  };

  return (
    <Dialog open={open} disableEscapeKeyDown fullWidth maxWidth="sm">
      <DialogContent
        sx={{
          p: 0,
        }}
      >
        <MDBox
          sx={{
            px: 3,
            py: 2.5,
            maxHeight: "75vh",
            overflowY: "auto",
          }}
        >
          <MDTypography
            sx={{
              fontSize: "20px",
              fontWeight: 600,
              mb: 2,
            }}
          >
            Send Template Message
          </MDTypography>

          <FormControl fullWidth>
            <InputLabel>Select template</InputLabel>

            <Select
              value={selectedTemplate}
              label="Select template"
              onChange={handleTemplateChange}
              sx={{
                padding: "12px",
                paddingLeft: "3px",
              }}
            >
              {templates.map((item) => (
                <MenuItem key={item.reference} value={item.reference}>
                  <MDBox>
                    <MDTypography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                      }}
                    >
                      {item.title}
                    </MDTypography>

                    <MDTypography
                      sx={{
                        fontSize: "11px",
                        color: "text.secondary",
                      }}
                    >
                      {item.category} · {item.language}
                    </MDTypography>
                  </MDBox>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {template && (
            <>
              <MDBox
                sx={{
                  mt: 2.5,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  backgroundColor: "#f8f9fa",
                }}
              >
                <MDTypography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "text.secondary",
                    mb: 1.5,
                  }}
                >
                  TEMPLATE PREVIEW
                </MDTypography>

                <MDBox
                  sx={{
                    backgroundColor: "#ffffff",
                    borderRadius: "8px",
                    p: 2,
                    border: "1px solid #eeeeee",
                  }}
                >
                  {template.components?.map((component, index) => {
                    const type = component.type?.toUpperCase();

                    if (type === "HEADER") {
                      return (
                        <MDTypography
                          key={index}
                          sx={{
                            fontSize: "15px",
                            fontWeight: 600,
                            mb: 1.5,
                          }}
                        >
                          {replaceVariables(component.text)}
                        </MDTypography>
                      );
                    }

                    if (type === "BODY") {
                      return (
                        <MDTypography
                          key={index}
                          sx={{
                            fontSize: "14px",
                            lineHeight: 1.6,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {replaceVariables(component.text)}
                        </MDTypography>
                      );
                    }

                    if (type === "FOOTER") {
                      return (
                        <MDTypography
                          key={index}
                          sx={{
                            fontSize: "11px",
                            color: "text.secondary",
                            mt: 1.5,
                          }}
                        >
                          {component.text}
                        </MDTypography>
                      );
                    }

                    return null;
                  })}
                </MDBox>
              </MDBox>

              {templateVariables.length > 0 && (
                <MDBox sx={{ mt: 2.5 }}>
                  <MDTypography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      mb: 1.5,
                    }}
                  >
                    Variable Mapping
                  </MDTypography>

                  <MDBox
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  >
                    {templateVariables.map((variable, index) => {
                      const mapping = variableMappings[variable] || {
                        type: "",
                        value: "",
                      };

                      return (
                        <MDBox
                          key={variable}
                          sx={{
                            p: 1.5,
                            borderBottom:
                              index < templateVariables.length - 1 ? "1px solid #e0e0e0" : "none",
                          }}
                        >
                          <MDTypography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 600,
                              mb: 1.8,
                            }}
                          >
                            {`{{${variable}}}`}
                          </MDTypography>

                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Map to</InputLabel>

                            <Select
                              value={mapping.type}
                              label="Map to"
                              onChange={(event) =>
                                handleVariableTypeChange(variable, event.target.value)
                              }
                              sx={{
                                padding: "12px",
                                paddingLeft: "3px",
                              }}
                            >
                              <MenuItem value="contact_name">Contact Name</MenuItem>
                              <MenuItem value="contact_phone">Contact Phone Number</MenuItem>
                              <MenuItem value="custom">Custom Value</MenuItem>
                            </Select>
                          </FormControl>

                          <MDBox>
                            {mapping.type === "contact_name" && (
                              <TextField
                                fullWidth
                                label="Contact Name"
                                value={mapping.value}
                                disabled
                              />
                            )}

                            {mapping.type === "contact_phone" && (
                              <TextField
                                fullWidth
                                label="Phone Number"
                                value={mapping.value}
                                disabled
                              />
                            )}

                            {mapping.type === "custom" && (
                              <TextField
                                fullWidth
                                label="Custom Value"
                                value={mapping.value}
                                placeholder="Enter value"
                                onChange={(event) =>
                                  handleVariableValueChange(variable, event.target.value)
                                }
                              />
                            )}
                          </MDBox>
                        </MDBox>
                      );
                    })}
                  </MDBox>
                </MDBox>
              )}
            </>
          )}
        </MDBox>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <MDButton variant="outlined" color="dark" onClick={handleCancel}>
          Cancel
        </MDButton>

        <MDButton variant="contained" color="dark" disabled={!canSend()} onClick={handleSend}>
          Send Template
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

export default WhatsAppTemplateComposer;
